import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Text } from '@BICE_Vida/components';

import styles from './PricesAndPlansTypes.module.scss';
import componentsId from '../../../utils/componentsId';
import { availablePlanCard, landingText, pricesAndPlansText } from '../../../utils/generalTexts';
import { formatMoney } from '../../../utils/format';

import { UF as UFType } from '../../../types/uf';
import {
  AvailablePlan,
  AvailablePlans as AvailablePlansType,
  AvailablePlan as AvailablePlanType,
} from '../../../types/availablePlans';
import { delayReviewAmount, TypeCoverage } from '../../../utils/constants';

import * as ufSelectors from '../../../redux/uf/selectors';
import * as plansSelectors from '../../../redux/plans/selectors';
import * as wizardActions from '../../../redux/wizard/actions';
import * as wizardSelectors from '../../../redux/wizard/selectors';
import AvailablePlanCard from '@/components/AvailablePlanCard';
import { trackEvent } from '@/utils/dataLayer/baseDataLayer';
import { planPageBaseLayer, planPageEvents } from '@/utils/dataLayer/planPage';
import AdditionalCoverages from '@/components/Home/AdditionalCoverages';
import AvailablePlanModal from '@/components/AvailablePlanModal';
import { CoverageResume } from '@/src/types/coverages';

const PricesAndPlansTypes = () => {
  const dispatch = useDispatch();
  const [coveragesModalDetails, setCoveragesModalDetails] = useState<{
    show: boolean;
    type: string | null;
    plan: {
      base: AvailablePlan;
      resume: CoverageResume;
      theme: 'primary' | 'secondary';
    } | null;
  }>({
    type: null,
    show: false,
    plan: null,
  });

  const UF: UFType = useSelector(ufSelectors.getUF);
  const today = UF.date && UF.date.split('-').reverse().join('/');
  const availablePlans: AvailablePlansType = useSelector(plansSelectors.getAvailablePlans);
  const planSelected: string = useSelector(wizardSelectors.getPrePlanSelectedRestored);
  const coveragesValue = useSelector(plansSelectors.getDentalCat());

  const dentalBasePlan = useSelector(plansSelectors.getPlanByCoverage(TypeCoverage.dental));
  const catastrophicBasePlan = useSelector(
    plansSelectors.getPlanByCoverage(TypeCoverage.catastrophic)
  );
  const deceaseBasePlan = useSelector(plansSelectors.getPlanByCoverage(TypeCoverage.decease));

  const setPlanSelected = (id: string, type: string): void | undefined => {
    if (planSelected !== id) {
      dispatch(wizardActions.setLoading(true));
      dispatch(wizardActions.setPrePlanSelected(id));
      dispatch(wizardActions.setPlanSelected(id));
      dispatch(wizardActions.setPrePlanType(type));

      setTimeout(() => {
        dispatch(wizardActions.setLoading(false));
      }, delayReviewAmount);
    }
  };

  const coveragesCard = useMemo(() => {
    return landingText.pymeValue.cards
      .filter((card) => !!coveragesValue[card.id as keyof typeof coveragesValue])
      .map((card) => ({
        ...card,
        title: card.title(UF, coveragesValue[card.id as keyof typeof coveragesValue] || 0),
        onClick: () => handleButtonClick(card.id),
      }));
  }, [UF, coveragesValue]);

  const availablePlansFiltered = availablePlans.filter(
    (card: AvailablePlanType) => card.base !== null && card.hidePlan !== true
  );

  useEffect(() => {
    if (availablePlans) {
      setPlanSelected(availablePlansFiltered[0]?._id, availablePlansFiltered[0]?.type);
    }
  }, [availablePlans]);

  const handleCloseCoveragesModal = () => {
    setCoveragesModalDetails({ show: false, plan: null, type: null });
    document.documentElement.style.overflow = 'auto';
  };

  const handleButtonClick = useCallback(
    (id?: string) => {
      if (id === 'catastrophic' && catastrophicBasePlan) {
        setCoveragesModalDetails({
          show: true,
          plan: {
            base: {
              ...catastrophicBasePlan?.plan,
              description: catastrophicBasePlan?.plan?.description.replace('+', '') ?? '',
              price: { uf: catastrophicBasePlan?.cost || 0 },
            },
            resume: catastrophicBasePlan,
            theme: 'primary' as const,
          },
          type: 'catastrophic',
        });
      } else if (id === 'dental' && dentalBasePlan) {
        setCoveragesModalDetails({
          show: true,
          plan: {
            base: {
              ...dentalBasePlan?.plan,

              description: dentalBasePlan?.plan?.description.replace('+', '') ?? '',
              price: { uf: dentalBasePlan?.cost || 0 },
            },
            resume: dentalBasePlan,
            theme: 'primary' as const,
          },
          type: 'dental',
        });
      } else if (id === 'decease' && deceaseBasePlan) {
        setCoveragesModalDetails({
          show: true,
          plan: {
            base: {
              ...deceaseBasePlan?.plan,
              description: deceaseBasePlan?.plan?.description.replace('+', '') ?? '',
              price: { uf: deceaseBasePlan?.cost || 0 },
            },
            resume: deceaseBasePlan,
            theme: 'primary' as const,
          },
          type: 'decease',
        });
      }
    },
    [dentalBasePlan, catastrophicBasePlan, deceaseBasePlan, setCoveragesModalDetails]
  );

  const handlePlanSelectedFromModal = (id: string, type: string, description: string) => {
    setPlanSelected(id, type);
  };

  useEffect(() => {
    if (coveragesModalDetails.show) {
      if (coveragesModalDetails.type === 'catastrophic' && catastrophicBasePlan) {
        handleButtonClick('catastrophic');
      } else if (coveragesModalDetails.type === 'dental' && dentalBasePlan) {
        handleButtonClick('dental');
      } else if (coveragesModalDetails.type === 'decease' && deceaseBasePlan) {
        handleButtonClick('decease');
      }
    }
  }, [planSelected]);

  return (
    <section id={componentsId.pricesAndPlans.types.section} className={styles.plan_types_section}>
      {coveragesModalDetails.plan && coveragesModalDetails.show && (
        <AvailablePlanModal
          isOpen={coveragesModalDetails.show}
          handleClose={handleCloseCoveragesModal}
          plan={coveragesModalDetails.plan.base}
          coverages={coveragesModalDetails.plan.resume.coverage}
          UF={UF}
          theme={coveragesModalDetails.plan.theme}
          skipAssistances
          availablePlans={
            coveragesModalDetails.type !== 'decease' ? availablePlansFiltered : undefined
          }
          setPlanSelected={
            coveragesModalDetails.type !== 'decease' ? handlePlanSelectedFromModal : undefined
          }
          isSelectedCoverage={coveragesModalDetails.show && coveragesModalDetails.plan !== null}
        />
      )}
      <div className={styles.plan_types_container}>
        <div className={styles.plan_types_cards}>
          {availablePlansFiltered.map((card, index: number) => (
            <AvailablePlanCard
              id={`${card.base}_card`}
              idx={index}
              customClass={styles.available_plans_card}
              availablePlan={card}
              UF={UF}
              key={index}
              btnAction={{
                id: `${card.base}_btn`,
                text: availablePlanCard.btn,
                onClickEvent: () => {
                  trackEvent({
                    eventParams: {
                      ...planPageBaseLayer,
                      ...planPageEvents.clickPlanDetails(card.description),
                    },
                  });
                },
              }}
            />
          ))}
        </div>
      </div>

      <div className={styles.additional_coverages_container}>
        <AdditionalCoverages
          customClass={styles.additional_coverages_custom}
          cards={coveragesCard}
        />
        <Text className={styles.info_warning_uf} fontTypo="caption">
          {pricesAndPlansText.types.additionalCoverages.warningUF(formatMoney(UF.value), today)}
        </Text>
      </div>
    </section>
  );
};

export default PricesAndPlansTypes;
